import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, X, MapPin, Globe, Home, ShoppingBag, Tag,
  User, ChevronDown, ChevronUp, Menu, Navigation2,
  SlidersHorizontal, ArrowRight, Loader2, Store,
} from "lucide-react";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchAllCommercePartners } from "../../redux/commercePartnersSlice";
import { Notifications, SupportAgent, ChatBubble } from "@mui/icons-material";
import {
  listenToUserNotifications,
  stopUserNotificationsListener,
} from "../../redux/notificationsSlice";

/* ═══════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════ */
const RADIUS_OPTIONS = [5, 10, 25, 50, 100];
const DEFAULT_RADIUS = 25;

const distKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const fmtDist = (km: number) =>
  km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;

/* ═══════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════ */
const T = {
  bg:       "#FFFFFF",
  bgPage:   "#F7F8FC",
  border:   "#E8ECF5",
  muted:    "#8A95B0",
  ink:      "#0D1025",
  blue:     "#0A66C2",
  blueLight:"#4A90D9",
  blueDim:  "rgba(10,102,194,0.08)",
  red:      "#E50914",
  gold:     "#FEBA02",
  green:    "#16A34A",
  grad:     "linear-gradient(135deg,#0A66C2 0%,#003580 100%)",
};

/* ═══════════════════════════════════════════════════════
   CARD BORDER RADIUS CYCLE
═══════════════════════════════════════════════════════ */
const CARD_RADII = [
  "24px",
  "16px 28px 16px 28px",
  "32px",
  "28px 14px 28px 14px",
  "20px",
  "14px 32px 14px 32px",
  "28px",
  "20px 20px 36px 20px",
];

/* ═══════════════════════════════════════════════════════
   ACCENT COLORS PER CARD INDEX
═══════════════════════════════════════════════════════ */
const ACCENT_PALETTE = [
  { bg: "#EFF6FF", border: "#BFDBFE", dot: "#2563EB" },
  { bg: "#FFF7ED", border: "#FED7AA", dot: "#EA580C" },
  { bg: "#F0FDF4", border: "#BBF7D0", dot: "#16A34A" },
  { bg: "#FDF4FF", border: "#E9D5FF", dot: "#9333EA" },
  { bg: "#FFFBEB", border: "#FDE68A", dot: "#D97706" },
  { bg: "#FFF1F2", border: "#FECDD3", dot: "#E11D48" },
  { bg: "#F0FDFA", border: "#99F6E4", dot: "#0D9488" },
  { bg: "#F5F3FF", border: "#DDD6FE", dot: "#7C3AED" },
];

/* ═══════════════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: #F1F4FA; }
  ::-webkit-scrollbar-thumb { background: #C8D0E0; border-radius: 4px; }
  input, button, select { font-family: inherit; }

  @keyframes ms-spin { to { transform: rotate(360deg); } }
  @keyframes ms-ping {
    0%   { transform: scale(1); opacity: 0.9; }
    100% { transform: scale(2.2); opacity: 0; }
  }
  @keyframes ms-float {
    0%,100% { transform: translateY(0); }
    50%      { transform: translateY(-5px); }
  }
  @keyframes ms-slide-up {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .ms-card { animation: ms-slide-up 0.4s ease both; }
  .ms-card:hover .ms-card-arrow { transform: translateX(4px); }
  .ms-card:hover .ms-card-logo  { transform: scale(1.05); }
  .ms-radius-chip:hover { background: #EFF6FF !important; }
  .ms-cat-chip { transition: all 0.18s ease; }
  .ms-cat-chip:hover { transform: translateY(-1px); }
  .ms-store-card { transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease; }
  .ms-store-card:hover { transform: translateY(-4px); box-shadow: 0 20px 48px rgba(10,102,194,0.16) !important; }
  .ms-sort-item:hover { background: #EFF6FF; }
  .ms-search-wrap:focus-within { border-color: rgba(10,102,194,0.55) !important; box-shadow: 0 0 0 4px rgba(10,102,194,0.10) !important; }

  input::placeholder { color: rgba(138,149,176,0.65); }
`;

/* ═══════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════ */
const getInitials = (name: string) =>
  (name ?? "?").split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");

/* ═══════════════════════════════════════════════════════
   STORE CARD
═══════════════════════════════════════════════════════ */
const StoreCard: React.FC<{
  partner: any;
  distance: number | null;
  index: number;
  onClick: () => void;
}> = ({ partner, distance, index, onClick }) => {
  const accent = ACCENT_PALETTE[index % ACCENT_PALETTE.length];
  const radius = CARD_RADII[index % CARD_RADII.length];

  return (
    <motion.div
      className="ms-store-card ms-card"
      style={{
        animationDelay: `${Math.min(index * 0.055, 0.6)}s`,
        borderRadius: radius,
        background: "#fff",
        border: `1.5px solid ${accent.border}`,
        boxShadow: "0 4px 20px rgba(10,102,194,0.07)",
        cursor: "pointer",
        overflow: "hidden",
        position: "relative",
      }}
      onClick={onClick}
      whileTap={{ scale: 0.97 }}
    >
      {/* top accent strip */}
      <div style={{ height: 4, background: accent.dot }} />

      <div style={{ padding: "18px 18px 16px" }}>
        {/* header row */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 14 }}>

          {/* logo */}
          <div className="ms-card-logo" style={{ flexShrink: 0, transition: "transform 0.3s ease" }}>
            {partner.image ? (
              <img
                src={partner.image}
                alt={partner.name}
                style={{ width: 56, height: 56, borderRadius: 14, objectFit: "cover", border: `2px solid ${accent.border}` }}
              />
            ) : (
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `linear-gradient(135deg,${accent.dot}30,${accent.dot}18)`, border: `2px solid ${accent.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontFamily: "'Fraunces',serif", fontSize: 20, fontWeight: 900, color: accent.dot }}>
                  {getInitials(partner.name ?? "")}
                </span>
              </div>
            )}
          </div>

          {/* info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* name + live dot */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <span style={{ fontFamily: "'Fraunces',serif", fontWeight: 700, fontSize: 15, color: T.ink, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {partner.name ?? "Unnamed Store"}
              </span>
              {partner.isPublished && (
                <span style={{ position: "relative", flexShrink: 0 }}>
                  <span style={{ display: "block", width: 8, height: 8, borderRadius: "50%", background: T.green }} />
                  <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: T.green, animation: "ms-ping 1.8s ease-out infinite" }} />
                </span>
              )}
            </div>

            {/* location */}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <MapPin size={11} color={T.red} />
              <span style={{ fontSize: 11, color: T.muted, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {[partner.address?.city, partner.address?.country].filter(Boolean).join(", ") || "Location unknown"}
              </span>
            </div>
          </div>

          {/* arrow */}
          <div
            className="ms-card-arrow"
            style={{ flexShrink: 0, width: 30, height: 30, borderRadius: "50%", background: accent.bg, border: `1.5px solid ${accent.border}`, display: "flex", alignItems: "center", justifyContent: "center", transition: "transform 0.2s ease", alignSelf: "center" }}
          >
            <ArrowRight size={13} color={accent.dot} />
          </div>
        </div>

        {/* footer row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {distance !== null ? (
            <div style={{ display: "flex", alignItems: "center", gap: 5, background: T.blueDim, borderRadius: 999, padding: "4px 10px" }}>
              <Navigation2 size={10} color={T.blue} />
              <span style={{ fontSize: 11, fontWeight: 700, color: T.blue }}>{fmtDist(distance)} away</span>
            </div>
          ) : (
            <div />
          )}
          <span style={{ fontSize: 10, fontWeight: 600, color: accent.dot, background: accent.bg, padding: "3px 8px", borderRadius: 6 }}>
            Browse
          </span>
        </div>
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */
const MetaStores: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();

  const authUser      = useSelector((s: RootState) => s.auth.user);
  const authLoaded    = useSelector((s: RootState) => s.auth.authLoaded);
  const userId        = authUser?.userId;
  const partners      = useSelector((s: RootState) => (s as any).commercePartners?.partners ?? []);
  const loading       = useSelector((s: RootState) => (s as any).commercePartners?.loading ?? false);
  const notifications = useSelector((s: RootState) => s.notifications.notifications);
  const unreadCount   = useMemo(() => notifications.filter((n: any) => !n.read).length, [notifications]);

  const [isMobile,    setIsMobile]    = useState(window.innerWidth <= 768);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortMode,    setSortMode]    = useState<"nearby" | "az">("nearby");
  const [sortOpen,    setSortOpen]    = useState(false);
  const [navOpen,     setNavOpen]     = useState(false);
  const [radiusKm,    setRadiusKm]    = useState(DEFAULT_RADIUS);
  const [localOnly,   setLocalOnly]   = useState(true);

  const [userLat,     setUserLat]     = useState<number | null>(null);
  const [userLon,     setUserLon]     = useState<number | null>(null);
  const [userCity,    setUserCity]    = useState<string | null>(null);
  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [locLoading,  setLocLoading]  = useState(true);
  const [locDenied,   setLocDenied]   = useState(false);

  const sortBtnRef = useRef<HTMLButtonElement>(null);
  const [sortAnchor, setSortAnchor] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  useEffect(() => { dispatch(fetchAllCommercePartners() as any); }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    dispatch(listenToUserNotifications(userId));
    return () => { dispatch(stopUserNotificationsListener()); };
  }, [dispatch, userId]);

  /* geolocation */
  useEffect(() => {
    if (!navigator.geolocation) { setLocDenied(true); setLocLoading(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLat(pos.coords.latitude);
        setUserLon(pos.coords.longitude);
        setLocDenied(false);
        if ((window as any).google?.maps) {
          const gc = new (window as any).google.maps.Geocoder();
          gc.geocode(
            { location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
            (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                setUserCountry(get("country"));
              }
              setLocLoading(false);
            }
          );
        } else { setLocLoading(false); }
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setLocDenied(true);
        setLocLoading(false); setLocalOnly(false);
      }
    );
  }, []);

  /* enrich with distance */
  const enriched = useMemo(() =>
    partners
      .filter((p: any) => p.isPublished && !p.deleted)
      .map((p: any) => {
        const lat = p.address?.latitude;
        const lon = p.address?.longitude;
        const dist =
          userLat !== null && userLon !== null && lat != null && lon != null
            ? distKm(userLat, userLon, lat, lon) : null;
        return { ...p, dist };
      }),
    [partners, userLat, userLon]
  );

  /* filter + sort */
  const filtered = useMemo(() => {
    let list = enriched;

    if (localOnly && userLat !== null && userLon !== null) {
      list = list.filter((p: any) => p.dist !== null && p.dist <= radiusKm);
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((p: any) =>
        p.name?.toLowerCase().includes(q) ||
        p.address?.city?.toLowerCase().includes(q) ||
        p.address?.country?.toLowerCase().includes(q)
      );
    }

    if (sortMode === "nearby") {
      list = [...list].sort((a: any, b: any) => {
        if (a.dist === null && b.dist === null) return 0;
        if (a.dist === null) return 1;
        if (b.dist === null) return -1;
        return a.dist - b.dist;
      });
    } else {
      list = [...list].sort((a: any, b: any) => (a.name ?? "").localeCompare(b.name ?? ""));
    }
    return list;
  }, [enriched, localOnly, radiusKm, userLat, userLon, searchQuery, sortMode]);

  const hasLocation = !locLoading && !locDenied && userLat !== null;

  const toggleSort = () => {
    if (sortOpen) { setSortOpen(false); setSortAnchor(null); return; }
    if (sortBtnRef.current) {
      const r = sortBtnRef.current.getBoundingClientRect();
      setSortAnchor({ top: r.bottom + 6, left: r.left });
    }
    setSortOpen(true);
  };

  /* ── LOCATION PILL ─────────────────────────────────── */
  const LocationPill = () => {
    if (locLoading) return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#F1F5FB", borderRadius: 8, padding: "5px 10px", border: "1px solid #E0E8F5" }}>
        <Loader2 size={12} color={T.muted} style={{ animation: "ms-spin 1s linear infinite" }} />
        <span style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>Finding location…</span>
      </div>
    );
    if (locDenied) return (
      <button style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(229,9,20,0.07)", borderRadius: 8, padding: "5px 10px", border: "1px solid rgba(229,9,20,0.22)", cursor: "pointer", color: T.red, fontSize: 11, fontWeight: 700 }}>
        <MapPin size={11} />Location off
      </button>
    );
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(22,163,74,0.08)", borderRadius: 8, padding: "5px 10px", border: "1px solid rgba(22,163,74,0.22)" }}>
        <span style={{ position: "relative", width: 8, height: 8 }}>
          <span style={{ display: "block", width: 8, height: 8, borderRadius: "50%", background: T.green }} />
          <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: T.green, animation: "ms-ping 2s ease-out infinite" }} />
        </span>
        <MapPin size={11} color={T.green} />
        <span style={{ fontSize: 11, color: T.green, fontWeight: 700 }}>{userCity ?? userCountry ?? "Nearby"}</span>
      </div>
    );
  };

  /* ── NAV DROPDOWN ──────────────────────────────────── */
  const NavMenu = () => (
    <AnimatePresence>
      {navOpen && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 499 }} onClick={() => setNavOpen(false)} />
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
            style={{ position: "fixed", top: 70, right: 16, width: 210, zIndex: 500, background: "#fff", borderRadius: 18, boxShadow: "0 24px 60px rgba(0,0,0,0.14)", border: "1px solid #E8ECF5", overflow: "hidden" }}
          >
            {[
              { label: "Profile", icon: <User size={14} />,           path: `/profile/${userId}` },
              { label: "Orders",  icon: <ShoppingBag size={14} />,     path: `/MetaClientDashboard/${userId}` },
              { label: "Sell",    icon: <Tag size={14} />,             path: `/metaonboarding/${userId}` },
              { label: "Support", icon: <SupportAgent style={{ fontSize: 14 }} />, path: `/CustomerSupport/${userId}` },
              { label: "Chat",    icon: <ChatBubble style={{ fontSize: 14 }} />,   path: "/MessagesPreview" },
            ].map((item) => (
              <button key={item.label} onClick={() => { navigate(item.path); setNavOpen(false); }}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", background: "none", border: "none", cursor: "pointer", width: "100%", fontSize: 13, fontWeight: 700, color: T.ink, borderBottom: "1px solid #F4F6FB" }}>
                <span style={{ color: T.blue }}>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ── SORT MENU ─────────────────────────────────────── */
  const SortMenu = () => (
    <>
      {sortOpen && sortAnchor && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => { setSortOpen(false); setSortAnchor(null); }} />
          <div style={{ position: "fixed", top: sortAnchor.top, left: Math.min(sortAnchor.left, window.innerWidth - 210), background: "#fff", borderRadius: 14, boxShadow: "0 20px 56px rgba(0,0,0,0.16)", zIndex: 9999, minWidth: 200, overflow: "hidden", border: "1px solid #E8ECF5" }}>
            {[
              { key: "nearby", label: "Nearest first" },
              { key: "az",     label: "Name: A → Z" },
            ].map((opt) => (
              <div key={opt.key} className="ms-sort-item"
                onClick={() => { setSortMode(opt.key as any); setSortOpen(false); setSortAnchor(null); }}
                style={{ padding: "11px 18px", fontSize: 13, fontWeight: sortMode === opt.key ? 700 : 500, color: sortMode === opt.key ? T.blue : T.ink, background: sortMode === opt.key ? "#EFF6FF" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #F4F6FB" }}>
                {opt.label}
                {sortMode === opt.key && <span style={{ color: T.blue, fontWeight: 900 }}>✓</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );

  /* ── HEADER ────────────────────────────────────────── */
  const Header = () => (
    <div style={{ position: "sticky", top: 0, zIndex: 200, background: "#fff", borderBottom: "1px solid #EBF0FA", boxShadow: "0 2px 20px rgba(10,102,194,0.07)" }}>
      {/* row 1 */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 20px 8px" }}>
        <button onClick={() => navigate(-1)}
          style={{ width: 36, height: 36, borderRadius: "50%", background: T.blueDim, border: "1.5px solid #DCE8FF", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <Home size={15} color={T.blue} />
        </button>

        <span style={{ fontFamily: "'Fraunces',serif", fontSize: isMobile ? 18 : 22, fontWeight: 900, color: T.ink, flexShrink: 0 }}>
          Meta<em style={{ fontStyle: "italic", color: T.blue }}>Stores</em>
        </span>

        <LocationPill />
        <div style={{ flex: 1 }} />

        {/* notifications */}
        <div style={{ position: "relative", cursor: "pointer", flexShrink: 0 }} onClick={() => userId && navigate(`/notifications/${userId}`)}>
          <Notifications style={{ fontSize: 22, color: T.muted }} />
          {unreadCount > 0 && (
            <span style={{ position: "absolute", top: -4, right: -4, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {unreadCount}
            </span>
          )}
        </div>

        {/* sort */}
        <button ref={sortBtnRef} onClick={toggleSort}
          style={{ display: "flex", alignItems: "center", gap: 5, background: sortOpen ? T.blue : "#F1F5FB", border: `1.5px solid ${sortOpen ? T.blue : "#DCE8FF"}`, borderRadius: 9, padding: "7px 12px", color: sortOpen ? "#fff" : T.ink, fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
          <SlidersHorizontal size={13} />
          {!isMobile && <span>{sortMode === "nearby" ? "Nearest" : "A–Z"}</span>}
          {sortOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>

        {/* menu */}
        <button onClick={() => setNavOpen((v) => !v)}
          style={{ width: 36, height: 36, borderRadius: "50%", background: navOpen ? T.blue : "#F1F5FB", border: "1.5px solid #DCE8FF", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          {navOpen ? <X size={15} color="#fff" /> : <Menu size={15} color={T.ink} />}
        </button>
      </div>

      {/* row 2 — search */}
      <div style={{ padding: "0 20px 10px" }}>
        <div className="ms-search-wrap"
          style={{ display: "flex", alignItems: "center", background: "#F7F9FD", borderRadius: 12, overflow: "hidden", border: "1.5px solid #E0E8F5", transition: "border-color 0.2s, box-shadow 0.2s" }}>
          <Search size={15} color={T.muted} style={{ marginLeft: 14, flexShrink: 0 }} />
          <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search stores, cities…"
            style={{ flex: 1, border: "none", outline: "none", padding: "10px 12px", fontSize: 13, color: T.ink, background: "transparent", fontWeight: 500 }} />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 12px", color: T.muted }}>
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* row 3 — radius chips (only when location granted) */}
      {hasLocation && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 20px 12px", overflowX: "auto", scrollbarWidth: "none" }}>
          <Navigation2 size={13} color={T.muted} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, flexShrink: 0 }}>Radius:</span>
          {RADIUS_OPTIONS.map((r) => {
            const isActive = localOnly && radiusKm === r;
            return (
              <button key={r} className="ms-radius-chip"
                onClick={() => { setRadiusKm(r); setLocalOnly(true); }}
                style={{ padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: "pointer", border: `1.5px solid ${isActive ? T.blue : "#DCE8FF"}`, background: isActive ? T.blue : "#fff", color: isActive ? "#fff" : T.muted, flexShrink: 0, transition: "all 0.18s ease" }}>
                {r} km
              </button>
            );
          })}
          <button onClick={() => setLocalOnly((v) => !v)}
            style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: "pointer", border: `1.5px solid ${!localOnly ? "#334155" : "#DCE8FF"}`, background: !localOnly ? "#334155" : "#fff", color: !localOnly ? "#fff" : T.muted, flexShrink: 0, transition: "all 0.18s ease" }}>
            <Globe size={11} />All
          </button>
        </div>
      )}

      {/* gradient rule */}
      <div style={{ height: 2, background: "linear-gradient(90deg,#0A66C2,#003580,#FEBA02,#0A66C2)", backgroundSize: "200% 100%" }} />
    </div>
  );

  /* ── RESULTS BAR ──────────────────────────────────── */
  const ResultsBar = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 4, height: 20, borderRadius: 2, background: T.grad }} />
        <span style={{ fontFamily: "'Fraunces',serif", fontSize: 16, fontWeight: 700, color: T.ink }}>
          {loading ? "Loading stores…" : `${filtered.length} store${filtered.length !== 1 ? "s" : ""} found`}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {!locLoading && <span style={{ width: 7, height: 7, borderRadius: "50%", background: locDenied ? T.red : T.green, display: "inline-block" }} />}
        <span style={{ fontSize: 11, fontWeight: 600, color: T.muted }}>
          {locDenied ? "Location off" : localOnly && hasLocation ? `Within ${radiusKm} km` : "All locations"}
          {" · "}
          {sortMode === "nearby" ? "Nearest first" : "A–Z"}
        </span>
      </div>
    </div>
  );

  /* ── EMPTY ─────────────────────────────────────────── */
  const Empty = () => (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 40px", gap: 14, textAlign: "center" }}>
      <div style={{ width: 80, height: 80, borderRadius: 28, background: "#F1F5FB", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8, animation: "ms-float 3s ease-in-out infinite" }}>
        <ShoppingBag size={36} color={T.muted} />
      </div>
      <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 20, fontWeight: 900, color: T.ink }}>
        {locDenied ? "Location needed" : "No stores found"}
      </h3>
      <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.6, maxWidth: 320 }}>
        {locDenied
          ? "Enable location permissions to find stores near you."
          : searchQuery
          ? `No results for "${searchQuery}"`
          : localOnly
          ? `No stores within ${radiusKm} km. Try a larger radius or show all.`
          : "No stores match your current filters."}
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
        {searchQuery && (
          <button onClick={() => setSearchQuery("")}
            style={{ padding: "10px 22px", borderRadius: 999, background: T.grad, border: "none", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer" }}>
            Clear search
          </button>
        )}
        {localOnly && hasLocation && (
          <button onClick={() => setLocalOnly(false)}
            style={{ padding: "10px 22px", borderRadius: 999, background: "#F1F5FB", border: "1.5px solid #DCE8FF", color: T.blue, fontWeight: 800, fontSize: 13, cursor: "pointer" }}>
            Show all stores
          </button>
        )}
      </div>
    </div>
  );

  /* ── LOADING SKELETON ──────────────────────────────── */
  const LoadingGrid = () => (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill,minmax(320px,1fr))", gap: 16, padding: "0 20px" }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} style={{ borderRadius: CARD_RADII[i % CARD_RADII.length], height: 130, background: "linear-gradient(90deg,#F1F5FB 25%,#E8EDF5 50%,#F1F5FB 75%)", backgroundSize: "200% 100%", animation: "ms-slide-up 0.4s ease both", animationDelay: `${i * 0.08}s` }} />
      ))}
    </div>
  );

  /* ─────────────────────────────────────────────────── */
  return (
    <div style={{ minHeight: "100vh", background: T.bgPage, fontFamily: "'DM Sans',sans-serif" }}>
      <style>{GLOBAL_CSS}</style>
      <Header />
      <NavMenu />
      <SortMenu />

      {!loading && <ResultsBar />}

      <div style={{ padding: "0 20px 40px" }}>
        {loading ? (
          <LoadingGrid />
        ) : filtered.length === 0 ? (
          <Empty />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill,minmax(310px,1fr))", gap: 16 }}>
            {filtered.map((partner: any, index: number) => (
              <StoreCard
                key={partner.brandId}
                partner={partner}
                distance={partner.dist ?? null}
                index={index}
                onClick={() => navigate(`/MetaStoreHome/${partner.brandId}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MetaStores;